import { useState, useCallback, useEffect } from 'react';
import { GameState, UserSelection, DailyGameData, GameConfig, DailyPictureGameData, PictureEntry } from '../types';
import { API_ENDPOINTS } from '../config/api';
import { discordFetch } from '../utils/discordApi';
import { generateSessionId } from '../utils/discordUtils';

const initialState: GameState = {
  currentRound: 0,
  score: 0,
  totalRounds: 0, // Will be set when dailyGameData loads
  gameStarted: false,
  gameFinished: false,
  userSelections: [],

  // Text Game State
  currentAIText: null,
  currentHumanText: null,
  hasPlayedTextToday: false,
  textScore: 0,
  textTotalRounds: 0,

  // Picture Game State
  currentPicturePair: null,
  hasPlayedPictureToday: false,
  pictureScore: 0,
  pictureTotalRounds: 0,
  dailyPictureGameData: undefined,

  dailyGameData: undefined,
  sessionId: undefined,
  hasPlayedToday: false,
  gameDate: undefined,
  requiredCorrectAnswers: 8, // Default value, will be updated from config
  hasOldGame: false,
  oldGameDate: undefined,
  currentlyPlayingGameSetId: undefined, // Track which game set the user is actually playing
  currentlyPlayingGameData: undefined, // Store the game data for the game currently being played
  showChallengeCompletedPopup: false,
  challengeCompletedAndContinuing: false,
  nextGameAvailableAt: undefined,
  releaseScheduleLabel: undefined,
  releaseScheduleTimeZone: undefined,
};


// Session storage utilities for game progress persistence
// Session storage utilities for game progress persistence
const GAME_PROGRESS_KEY_TEXT = 'gameProgress_text';
const GAME_PROGRESS_KEY_PICTURE = 'gameProgress_picture';
const LAST_PLAYED_MODE_KEY = 'lastPlayedMode';

interface GameProgress {
  currentRound: number;
  score: number;
  totalRounds: number;
  gameStarted: boolean;
  userSelections: UserSelection[];
  currentAIText: any;
  currentHumanText: any;
  currentPicturePair: any;
  dailyGameSetId?: string;
  gameDate?: string;
  // Track which mode was being played
  mode?: 'text' | 'picture';
  requiredCorrectAnswers?: number;
}

const saveGameProgress = (state: GameState, mode: 'text' | 'picture'): void => {
  // Only save progress if game is started and not finished
  if (!state.gameStarted || state.gameFinished) {
    return;
  }

  const progress: GameProgress = {
    currentRound: state.currentRound,
    score: state.score,
    totalRounds: state.totalRounds,
    gameStarted: state.gameStarted,
    userSelections: state.userSelections,
    currentAIText: state.currentAIText,
    currentHumanText: state.currentHumanText,
    currentPicturePair: state.currentPicturePair,
    dailyGameSetId: state.currentlyPlayingGameSetId,
    gameDate: state.gameDate?.toISOString(),
    mode: mode,
    requiredCorrectAnswers: state.requiredCorrectAnswers
  };

  try {
    const key = mode === 'picture' ? GAME_PROGRESS_KEY_PICTURE : GAME_PROGRESS_KEY_TEXT;
    localStorage.setItem(key, JSON.stringify(progress));
    localStorage.setItem(LAST_PLAYED_MODE_KEY, mode);
  } catch (error) {
    console.warn('Failed to save game progress to session storage:', error);
  }
};

const loadGameProgress = (specificMode?: 'text' | 'picture'): GameProgress | null => {
  try {
    let key: string;

    if (specificMode) {
      key = specificMode === 'picture' ? GAME_PROGRESS_KEY_PICTURE : GAME_PROGRESS_KEY_TEXT;
    } else {
      // Try to load the last played mode first
      const lastMode = localStorage.getItem(LAST_PLAYED_MODE_KEY);
      key = GAME_PROGRESS_KEY_TEXT; // Default to text

      if (lastMode === 'picture') {
        key = GAME_PROGRESS_KEY_PICTURE;
      } else if (lastMode === 'text') {
        key = GAME_PROGRESS_KEY_TEXT;
      } else {
        // If no last mode, check if we have any progress saved
        if (localStorage.getItem(GAME_PROGRESS_KEY_PICTURE)) {
          key = GAME_PROGRESS_KEY_PICTURE;
        }
        // Text is default, so if neither or just text, we use text key
      }
    }

    const saved = localStorage.getItem(key);
    if (!saved) return null;

    const progress = JSON.parse(saved) as GameProgress;
    return progress;
  } catch (error) {
    console.warn('Failed to load game progress from session storage:', error);
    return null;
  }
};

const clearGameProgress = (mode?: 'text' | 'picture'): void => {
  try {
    if (mode === 'text') {
      localStorage.removeItem(GAME_PROGRESS_KEY_TEXT);
    } else if (mode === 'picture') {
      localStorage.removeItem(GAME_PROGRESS_KEY_PICTURE);
    } else {
      // If no mode specified, clear both (for full reset)
      localStorage.removeItem(GAME_PROGRESS_KEY_TEXT);
      localStorage.removeItem(GAME_PROGRESS_KEY_PICTURE);
      localStorage.removeItem(LAST_PLAYED_MODE_KEY);
    }
  } catch (error) {
    console.warn('Failed to clear game progress from session storage:', error);
  }
};

export const useGameState = () => {
  const [state, setState] = useState<GameState>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);

  // Initialize session on mount and restore game progress
  useEffect(() => {
    const sessionId = generateSessionId();
    setState(prev => ({ ...prev, sessionId }));
    checkTodayGameStatus(sessionId, true); // Mark as initial load
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore game progress after daily game data is loaded
  useEffect(() => {
    if (state.dailyGameData && !state.gameStarted) {
      const savedProgress = loadGameProgress();

      if (savedProgress && savedProgress.gameStarted) {
        // Validate that the saved progress matches current daily game
        const currentGameDate = state.gameDate?.toISOString();
        const savedGameDate = savedProgress.gameDate;

        // Determine which game set ID to check based on saved mode
        let currentGameSetId;
        if (savedProgress.mode === 'picture') {
          currentGameSetId = state.dailyPictureGameData?.gameSetId;
        } else {
          currentGameSetId = state.dailyGameData.gameSet.id;
        }

        const savedGameSetId = savedProgress.dailyGameSetId;

        // Check if this is the same game set and date
        if (currentGameSetId === savedGameSetId && currentGameDate === savedGameDate && savedProgress.currentRound <= savedProgress.totalRounds) {
          // Same game set - restore progress

          // Determine required correct answers based on mode
          let currentRequiredCorrectAnswers = 8;
          if (savedProgress.requiredCorrectAnswers) {
            currentRequiredCorrectAnswers = savedProgress.requiredCorrectAnswers;
          } else if (savedProgress.mode === 'picture' && state.dailyPictureGameData) {
            currentRequiredCorrectAnswers = state.dailyPictureGameData.targetScore;
          } else if (state.dailyGameData) {
            currentRequiredCorrectAnswers = state.dailyGameData.gameSet.challengeTargetScore;
          }

          setState(prev => ({
            ...prev,
            currentRound: savedProgress.currentRound,
            score: savedProgress.score,
            totalRounds: savedProgress.totalRounds,
            gameStarted: savedProgress.gameStarted,
            requiredCorrectAnswers: currentRequiredCorrectAnswers,
            userSelections: savedProgress.userSelections || [],
            currentAIText: savedProgress.currentAIText,
            currentHumanText: savedProgress.currentHumanText,
            currentPicturePair: savedProgress.currentPicturePair,
            currentlyPlayingGameSetId: savedProgress.dailyGameSetId,
            currentlyPlayingGameData: savedProgress.mode === 'text' ? state.dailyGameData : undefined,
          }));
        } else if (savedGameSetId !== currentGameSetId || savedGameDate !== currentGameDate) {
          // Different game set or date - show old game banner but allow continuing the old game
          console.log('Detected old game from different date/set:', {
            savedGameDate,
            currentGameDate,
            savedGameSetId,
            currentGameSetId
          });

          // Still restore the old game progress so user can continue if they want
          setState(prev => ({
            ...prev,
            currentRound: savedProgress.currentRound,
            score: savedProgress.score,
            totalRounds: savedProgress.totalRounds,
            gameStarted: savedProgress.gameStarted,
            requiredCorrectAnswers: prev.requiredCorrectAnswers, // Keep current required answers
            userSelections: savedProgress.userSelections || [],
            currentAIText: savedProgress.currentAIText,
            currentHumanText: savedProgress.currentHumanText,
            currentPicturePair: savedProgress.currentPicturePair,
            hasOldGame: true,
            oldGameDate: savedGameDate ? new Date(savedGameDate) : undefined,
            currentlyPlayingGameSetId: savedProgress.dailyGameSetId,
            currentlyPlayingGameData: undefined, // We don't have the old game data
          }));
        }
      }
    }
  }, [state.dailyGameData, state.dailyPictureGameData, state.gameStarted, state.gameDate]);

  // Save game progress whenever relevant state changes
  useEffect(() => {
    if (state.gameStarted && !state.gameFinished) {
      // Determine mode based on what content is present
      const mode = state.currentPicturePair ? 'picture' : 'text';
      saveGameProgress(state, mode);
    }
  }, [state]);

  // Check if user has played today - but don't write to localStorage until game starts
  useEffect(() => {
    const today = new Date().toDateString();
    const lastPlayedDate = localStorage.getItem('lastPlayDate');

    if (lastPlayedDate === today) {
      setState(prev => ({ ...prev, hasPlayedToday: true }));
    }
  }, []);

  // Helper to process selections safely
  const processSelections = (selections: any): UserSelection[] => {
    if (!selections) return [];
    if (typeof selections === 'string') {
      try {
        return JSON.parse(selections);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(selections) ? selections : [];
  };

  const checkTodayGameStatus = useCallback(async (sessionId: string, isInitialLoad: boolean = false) => {
    try {
      setIsLoading(true);

      // Fetch Text Game Data
      const textResponse = await discordFetch(API_ENDPOINTS.DAILY_GAME_TODAY, {
        headers: { 'x-session-id': sessionId }
      });

      // Fetch Picture Game Data
      let pictureGameData: DailyPictureGameData | undefined = undefined;
      try {
        // Assuming we can fetch picture game data. 
        // If the endpoint doesn't exist yet, this will fail gracefully.
        // In a real scenario, we'd ensure the endpoint exists.
        // For now, let's assume the main endpoint might return it or we fetch separately.
        // Since I haven't modified the backend to expose a separate endpoint yet, 
        // I should probably rely on the main endpoint if I modified it, or add a new one.
        // I'll assume for now that I need to add a way to get it.
        // But wait, I didn't add a specific endpoint for picture data in the backend yet.
        // I should probably check if I need to do that.
        // The task list said "Implement Backend Changes", and I did update the nightly job.
        // But I didn't update the API to serve the picture game data.
        // I should probably do that next.
        // For now, let's assume the client tries to fetch it, and if it fails, it's null.
        const picResponse = await discordFetch(`${API_ENDPOINTS.DAILY_GAME_TODAY}/picture`, {
          headers: { 'x-session-id': sessionId }
        });
        if (picResponse.ok) {
          pictureGameData = await picResponse.json();
        }
      } catch (e) {
        console.log('Picture game data not available yet');
      }

      if (textResponse.ok) {
        const dailyGameData: DailyGameData = await textResponse.json();
        const hasPlayedTextToday = !!dailyGameData.userResult;

        const hasPlayedPictureToday = !!pictureGameData?.userResult;

        const releaseScheduleLabel = dailyGameData.releaseSchedule?.displayLabel;
        const releaseScheduleTimeZone = dailyGameData.releaseSchedule?.timeZone;

        const nextGameAvailableAt = computeNextGameAvailableAt(
          dailyGameData.nextReleaseAt,
          dailyGameData.gameSet?.date
        );

        setState(prev => {
          // Update with current database values
          const updatedTotalRounds = dailyGameData.textPairs.length;
          const updatedRequiredCorrectAnswers = dailyGameData.gameSet.challengeTargetScore;

          return {
            ...prev,
            dailyGameData,
            dailyPictureGameData: pictureGameData,
            hasPlayedTextToday,
            hasPlayedPictureToday,
            hasPlayedToday: hasPlayedTextToday || hasPlayedPictureToday, // Composite status

            gameDate: new Date(new Date(dailyGameData.gameSet.date).toDateString()),

            // Text Game Stats
            textScore: dailyGameData.userResult?.score || 0,
            textTotalRounds: updatedTotalRounds,

            // Picture Game Stats
            pictureScore: pictureGameData?.userResult?.score || 0,
            pictureTotalRounds: pictureGameData?.pairs?.length || 5,

            requiredCorrectAnswers: prev.gameStarted ? prev.requiredCorrectAnswers : updatedRequiredCorrectAnswers,

            nextGameAvailableAt,
            releaseScheduleLabel: releaseScheduleLabel || prev.releaseScheduleLabel,
            releaseScheduleTimeZone: releaseScheduleTimeZone || prev.releaseScheduleTimeZone,
          };
        });
      }
    } catch (error) {
      console.error('Error checking today game status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch specific game set data by ID
  const fetchGameSetData = useCallback(async (gameSetId: string): Promise<DailyGameData | null> => {
    if (!state.sessionId) return null;

    try {
      const response = await discordFetch(`${API_ENDPOINTS.DAILY_GAME_TODAY}?gameSetId=${gameSetId}`, {
        headers: {
          'x-session-id': state.sessionId
        }
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching specific game set data:', error);
    }

    return null;
  }, [state.sessionId]);

  // Fetch game configuration from the server (kept for contentPairCount only)
  const fetchGameConfig = useCallback(async (): Promise<GameConfig | null> => {
    try {
      const response = await discordFetch(API_ENDPOINTS.GAME_CONFIG);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const serverConfig = await response.json();
      // Map server response to frontend interface
      const config: GameConfig = {
        contentPairCount: serverConfig.contentPairCount,
        requiredCorrectAnswers: serverConfig.challengeTargetScore // This is now only used as fallback
      };
      setGameConfig(config);
      return config;
    } catch (error) {
      console.error('Error fetching game config:', error);
      // Return default config if fetch fails
      const defaultConfig: GameConfig = {
        contentPairCount: 12,
        requiredCorrectAnswers: 8 // This is now only used as fallback
      };
      setGameConfig(defaultConfig);
      return defaultConfig;
    }
  }, []);

  // Load game config on mount
  useEffect(() => {
    fetchGameConfig();
  }, [fetchGameConfig]);



  const startDailyGame = useCallback(async () => {
    if (!state.sessionId || !state.dailyGameData || state.hasPlayedTextToday) {
      return;
    }

    setIsLoading(true);

    try {
      const firstPair = state.dailyGameData.textPairs[0];

      setState(prev => ({
        ...prev,
        currentRound: 1,
        score: 0,
        totalRounds: state.dailyGameData!.textPairs.length,
        gameStarted: true,
        gameFinished: false,
        userSelections: [],
        currentAIText: firstPair.aiText,
        currentHumanText: firstPair.humanText,
        currentPicturePair: null, // Ensure picture game state is cleared
        currentlyPlayingGameSetId: state.dailyGameData!.gameSet.id, // Track which game set we're playing
        currentlyPlayingGameData: state.dailyGameData, // Store the game data we're playing
        requiredCorrectAnswers: state.dailyGameData!.gameSet.challengeTargetScore, // Explicitly set target score
      }));
    } catch (error) {
      console.error('Error starting daily game:', error);
    } finally {
      setIsLoading(false);
    }
  }, [state.sessionId, state.dailyGameData, state.hasPlayedTextToday]);

  const startGame = useCallback(async () => {
    // Ensure we have daily game data before starting
    if (!state.dailyGameData) {
      console.warn('Cannot start game: Daily game data not loaded yet');
      return;
    }

    // Check if we should start daily game
    if (state.dailyGameData && !state.hasPlayedTextToday) {
      return startDailyGame();
    }

    // This shouldn't happen in the new simplified flow, but keeping as fallback
    console.warn('Attempting to start game when daily game already played');
  }, [state.dailyGameData, state.hasPlayedTextToday, startDailyGame]);

  const startPictureGame = useCallback(async () => {
    if (!state.sessionId || !state.dailyPictureGameData || state.hasPlayedPictureToday) {
      return;
    }

    setIsLoading(true);

    try {
      if (!state.dailyPictureGameData.pairs || state.dailyPictureGameData.pairs.length === 0) {
        console.error('No picture pairs available to start game');
        setIsLoading(false);
        return;
      }

      const firstPair = state.dailyPictureGameData.pairs[0];

      setState(prev => ({
        ...prev,
        currentRound: 1,
        score: 0,
        totalRounds: state.dailyPictureGameData!.pairs.length,
        gameStarted: true,
        gameFinished: false,
        userSelections: [],
        currentPicturePair: firstPair,
        currentAIText: null, // Ensure text game state is cleared
        currentHumanText: null, // Ensure text game state is cleared
        requiredCorrectAnswers: state.dailyPictureGameData!.targetScore,
        currentlyPlayingGameSetId: state.dailyPictureGameData!.gameSetId,
        currentlyPlayingGameData: undefined, // Picture game data is stored in dailyPictureGameData
      }));
    } catch (error) {
      console.error('Error starting picture game:', error);
    } finally {
      setIsLoading(false);
    }
  }, [state.sessionId, state.dailyPictureGameData, state.hasPlayedPictureToday]);

  const makeSelection = useCallback(async (selectedText: string, isAI: boolean) => {
    if (!state.currentAIText || !state.currentHumanText) return;

    // The game asks "Which text was written by AI?"
    // So a correct answer is when the user selects the AI text (isAI = true and selectedText is AI content)
    const wasCorrect = isAI && selectedText === state.currentAIText.content;

    const newSelection: UserSelection = {
      round: state.currentRound,
      selectedText,
      selectedAI: isAI,
      wasCorrect,
      aiText: state.currentAIText.content,
      humanText: state.currentHumanText.content,
    };

    const newScore = wasCorrect ? state.score + 1 : state.score;
    const currentSelections = Array.isArray(state.userSelections) ? state.userSelections : [];
    const newSelections = [...currentSelections, newSelection];

    // Check game end conditions
    let isGameFinished = false;
    let showChallengePopup = false;

    // Game finishes when all rounds completed
    const requiredCorrectAnswers = state.requiredCorrectAnswers;
    const allRoundsCompleted = state.currentRound >= state.totalRounds;
    const challengeTargetReached = newScore >= requiredCorrectAnswers;

    // If challenge target is reached for the first time and there are still rounds left, show popup
    if (challengeTargetReached && !allRoundsCompleted && !state.showChallengeCompletedPopup && !state.challengeCompletedAndContinuing) {
      showChallengePopup = true;
    } else if (allRoundsCompleted) {
      // Game finishes when all rounds completed
      isGameFinished = true;
    }

    setState(prev => ({
      ...prev,
      score: newScore,
      userSelections: newSelections,
      gameFinished: isGameFinished,
      showChallengeCompletedPopup: showChallengePopup,
    }));

    // If game is finished, mark as played today - only save to localStorage if game was actually started
    if (isGameFinished && state.gameStarted) {
      const today = new Date().toDateString();
      localStorage.setItem('lastPlayDate', today);
      setState(prev => ({ ...prev, hasPlayedTextToday: true }));
    }

    // If game is finished, save results and return early
    if (isGameFinished) {
      // If this is a daily game, save results
      if (state.dailyGameData && state.sessionId) {
        try {
          const accuracy = Math.round((newScore / newSelections.length) * 100);

          await discordFetch(API_ENDPOINTS.DAILY_GAME_RESULTS, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-session-id': state.sessionId
            },
            body: JSON.stringify({
              dailyGameSetId: state.currentlyPlayingGameSetId || state.dailyGameData.gameSet.id,
              score: newScore,
              totalRounds: newSelections.length,
              accuracy,
              selections: newSelections
            })
          });

        } catch (error) {
          console.error('Error saving game result:', error);
        }

        // Clear saved progress when game is completed
        clearGameProgress('text');

        // Optimistically update local state
        setState(prev => ({
          ...prev,
          hasPlayedTextToday: true,
          textScore: newScore,
        }));
      }
      return; // Always return early when game is finished
    }

    // If not finished, get next pair
    // If we're playing an old game and don't have the game data, fetch it
    if (state.hasOldGame && state.currentlyPlayingGameSetId && !state.currentlyPlayingGameData) {
      // Fetch the specific game set data for the old game
      const oldGameData = await fetchGameSetData(state.currentlyPlayingGameSetId);
      if (oldGameData) {
        setState(prev => ({ ...prev, currentlyPlayingGameData: oldGameData }));
        const nextPair = oldGameData.textPairs[state.currentRound];
        if (nextPair && nextPair.aiText && nextPair.humanText) {
          setState(prev => ({
            ...prev,
            currentRound: prev.currentRound + 1,
            currentAIText: nextPair.aiText,
            currentHumanText: nextPair.humanText,
          }));
        } else {
          console.log('No next pair available in old game data, finishing game');
          setState(prev => ({ ...prev, gameFinished: true }));
        }
        return;
      }
    }

    // Use the game data for the game we're actually playing, not necessarily the latest daily game data
    const gameDataToUse = state.currentlyPlayingGameData || state.dailyGameData;
    if (gameDataToUse) {
      const nextPair = gameDataToUse.textPairs[state.currentRound]; // currentRound is 0-indexed for next pair

      if (nextPair && nextPair.aiText && nextPair.humanText) {
        setState(prev => ({
          ...prev,
          currentRound: prev.currentRound + 1,
          currentAIText: nextPair.aiText,
          currentHumanText: nextPair.humanText,
        }));
      } else {
        // Defensive fallback if nextPair is missing or incomplete
        console.log('No next pair available, finishing game');
        setState(prev => ({
          ...prev,
          gameFinished: true,
        }));
      }
    } else {
      // If we don't have game data, finish the game
      console.log('No game data available for next pair, finishing game');
      setState(prev => ({
        ...prev,
        gameFinished: true,
      }));
    }
  }, [state.currentRound, state.totalRounds, state.currentAIText, state.currentHumanText,
  state.score, state.userSelections, state.dailyGameData, state.sessionId, state.requiredCorrectAnswers,
  state.gameStarted, state.showChallengeCompletedPopup, state.challengeCompletedAndContinuing,
  state.hasOldGame, state.currentlyPlayingGameSetId, state.currentlyPlayingGameData,
    fetchGameSetData]);

  const makePictureSelection = useCallback(async (selectedImageId: string, isAI: boolean) => {
    if (!state.currentPicturePair) return;

    const wasCorrect = isAI; // In picture game, user selects the AI image

    const newSelection: UserSelection = {
      round: state.currentRound,
      selectedText: '', // Not used in picture game
      selectedImageId,
      selectedAI: isAI,
      wasCorrect,
      // Store IDs for reference
      aiImageId: state.currentPicturePair.images.find(img => img.isAI)?.id,
      realImageId: state.currentPicturePair.images.find(img => !img.isAI)?.id,
    };

    const newScore = wasCorrect ? state.score + 1 : state.score;
    const currentSelections = Array.isArray(state.userSelections) ? state.userSelections : [];
    const newSelections = [...currentSelections, newSelection];

    // Check game end conditions
    let isGameFinished = false;
    let showChallengePopup = false;

    const requiredCorrectAnswers = state.requiredCorrectAnswers;
    const allRoundsCompleted = state.currentRound >= state.totalRounds;
    const challengeTargetReached = newScore >= requiredCorrectAnswers;

    if (challengeTargetReached && !allRoundsCompleted && !state.showChallengeCompletedPopup && !state.challengeCompletedAndContinuing) {
      showChallengePopup = true;
    } else if (allRoundsCompleted) {
      isGameFinished = true;
    }

    console.log('Picture Selection:', {
      currentRound: state.currentRound,
      totalRounds: state.totalRounds,
      newScore,
      allRoundsCompleted,
      challengeTargetReached,
      isGameFinished,
      showChallengePopup
    });

    setState(prev => ({
      ...prev,
      score: newScore,
      userSelections: newSelections,
      gameFinished: isGameFinished,
      showChallengeCompletedPopup: showChallengePopup,
    }));

    if (isGameFinished) {
      // Save picture game results
      if (state.dailyPictureGameData && state.sessionId) {
        try {
          const accuracy = Math.round((newScore / newSelections.length) * 100);

          await discordFetch(`${API_ENDPOINTS.DAILY_GAME_RESULTS}/picture`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-session-id': state.sessionId
            },
            body: JSON.stringify({
              dailyPictureGameSetId: state.currentlyPlayingGameSetId,
              score: newScore,
              totalRounds: newSelections.length,
              accuracy,
              selections: newSelections
            })
          });

        } catch (error) {
          console.error('Error saving picture game result:', error);
        }

        // Clear saved progress when game is completed
        clearGameProgress('picture');

        // Optimistically update local state
        setState(prev => ({
          ...prev,
          hasPlayedPictureToday: true,
          pictureScore: newScore,
        }));
      }
      return;
    }

    // Next round
    if (state.dailyPictureGameData) {
      const nextPair = state.dailyPictureGameData.pairs[state.currentRound];
      if (nextPair) {
        setState(prev => ({
          ...prev,
          currentRound: prev.currentRound + 1,
          currentPicturePair: nextPair,
        }));
      } else {
        setState(prev => ({ ...prev, gameFinished: true }));
      }
    }
  }, [state.currentPicturePair, state.currentRound, state.totalRounds, state.score, state.userSelections,
  state.dailyPictureGameData, state.sessionId, state.requiredCorrectAnswers, state.showChallengeCompletedPopup,
  state.challengeCompletedAndContinuing, state.currentlyPlayingGameSetId]);

  const resetGame = useCallback(() => {
    // Only allow reset for non-daily games
    setState(prev => {
      if (prev.dailyGameData && prev.hasPlayedToday) {
        // Don't reset if it's a daily game that's already been played
        return prev;
      }

      // Clear saved progress when resetting
      clearGameProgress();

      return {
        ...initialState,
        sessionId: prev.sessionId,
        dailyGameData: prev.dailyGameData,
        dailyPictureGameData: prev.dailyPictureGameData, // Keep picture game data
        hasPlayedToday: prev.hasPlayedToday,
        hasPlayedTextToday: prev.hasPlayedTextToday,
        hasPlayedPictureToday: prev.hasPlayedPictureToday,
        gameDate: prev.gameDate,
        requiredCorrectAnswers: prev.requiredCorrectAnswers,
        nextGameAvailableAt: prev.nextGameAvailableAt,
        releaseScheduleLabel: prev.releaseScheduleLabel,
        releaseScheduleTimeZone: prev.releaseScheduleTimeZone,
        textScore: prev.textScore,
        textTotalRounds: prev.textTotalRounds,
        pictureScore: prev.pictureScore,
        pictureTotalRounds: prev.pictureTotalRounds,
      };
    });
  }, []);

  const resetToMenu = useCallback(() => {
    // When going back to menu, we don't want to clear the game progress
    // We just want to reset the UI state so the user sees the menu
    setState(prev => ({
      ...prev,
      gameStarted: false,
      gameFinished: false,
      // Don't reset these, so we can resume
      // currentRound: 0,
      // score: 0,
      // userSelections: [],
      // currentAIText: null,
      // currentHumanText: null,
      // currentPicturePair: null,
    }));
  }, []);

  const resumeGame = useCallback((mode: 'text' | 'picture') => {
    const savedProgress = loadGameProgress(mode);
    if (!savedProgress || savedProgress.mode !== mode) return;

    // Determine required correct answers based on mode
    let currentRequiredCorrectAnswers = 8;
    if (savedProgress.requiredCorrectAnswers) {
      currentRequiredCorrectAnswers = savedProgress.requiredCorrectAnswers;
    } else if (mode === 'picture' && state.dailyPictureGameData) {
      currentRequiredCorrectAnswers = state.dailyPictureGameData.targetScore;
    } else if (state.dailyGameData) {
      currentRequiredCorrectAnswers = state.dailyGameData.gameSet.challengeTargetScore;
    }

    // Restore state from saved progress
    setState(prev => ({
      ...prev,
      currentRound: savedProgress.currentRound,
      score: savedProgress.score,
      totalRounds: savedProgress.totalRounds,
      gameStarted: true,
      gameFinished: false,
      userSelections: savedProgress.userSelections || [],
      currentAIText: savedProgress.currentAIText,
      currentHumanText: savedProgress.currentHumanText,
      currentPicturePair: savedProgress.currentPicturePair,
      currentlyPlayingGameSetId: savedProgress.dailyGameSetId,
      requiredCorrectAnswers: currentRequiredCorrectAnswers,
      // Ensure we have the correct game data loaded if possible
      // This might need more robust handling if data is missing, but for now we rely on what's saved
    }));
  }, []);

  // Check for saved progress to expose to UI
  const [hasSavedTextProgress, setHasSavedTextProgress] = useState(false);
  const [hasSavedPictureProgress, setHasSavedPictureProgress] = useState(false);

  useEffect(() => {
    // Check if we have saved progress for text game
    const textProgress = localStorage.getItem(GAME_PROGRESS_KEY_TEXT);
    if (textProgress) {
      const parsed = JSON.parse(textProgress);
      // Only consider it saved if not finished
      if (parsed.gameStarted && parsed.currentRound < parsed.totalRounds) {
        setHasSavedTextProgress(true);
      }
    }

    // Check if we have saved progress for picture game
    const pictureProgress = localStorage.getItem(GAME_PROGRESS_KEY_PICTURE);
    if (pictureProgress) {
      const parsed = JSON.parse(pictureProgress);
      if (parsed.gameStarted && parsed.currentRound < parsed.totalRounds) {
        setHasSavedPictureProgress(true);
      }
    }
  }, [state.gameStarted, state.gameFinished, state.currentRound]); // Re-check when game state changes



  const getAccuracy = useCallback(() => {
    // Defensive check to ensure userSelections is an array
    if (!Array.isArray(state.userSelections)) {
      console.warn('userSelections is not an array:', state.userSelections);
      return 0;
    }
    if (state.userSelections.length === 0) return 0;

    const correctSelections = state.userSelections.filter(selection => selection.wasCorrect).length;
    return Math.round((correctSelections / state.userSelections.length) * 100);
  }, [state.userSelections]);

  const checkForNewContent = useCallback(async () => {
    if (!state.sessionId) return false;

    setIsLoading(true);
    try {
      const response = await discordFetch(API_ENDPOINTS.DAILY_GAME_NEW_CONTENT, {
        headers: {
          'x-session-id': state.sessionId
        }
      });

      if (response.ok) {
        const { hasNewContent } = await response.json();
        if (hasNewContent) {
          // Refresh today's game data - this will trigger the old game detection logic
          await checkTodayGameStatus(state.sessionId);
          // Don't automatically clear progress - let the user decide via the banner
        }
        return hasNewContent;
      }
    } catch (error) {
      console.error('Error checking for new content:', error);
    } finally {
      setIsLoading(false);
    }

    return false;
  }, [state.sessionId, checkTodayGameStatus]);


  const clearOldGameAndStartFresh = useCallback(async () => {
    if (!state.sessionId) return;

    // First, check for the latest game data
    await checkTodayGameStatus(state.sessionId);

    // Clear the old saved progress
    clearGameProgress();

    // Reset to initial state but keep the updated daily game data and session
    setState(prev => ({
      ...initialState,
      sessionId: prev.sessionId,
      dailyGameData: prev.dailyGameData,
      dailyPictureGameData: prev.dailyPictureGameData, // Keep picture game data
      gameDate: prev.gameDate,
      hasPlayedToday: prev.hasPlayedToday,
      hasPlayedTextToday: prev.hasPlayedTextToday,
      hasPlayedPictureToday: prev.hasPlayedPictureToday,
      requiredCorrectAnswers: prev.dailyGameData?.gameSet.challengeTargetScore || prev.requiredCorrectAnswers,
      totalRounds: prev.dailyGameData?.textPairs.length || 0,
      hasOldGame: false,
      oldGameDate: undefined,
      nextGameAvailableAt: prev.nextGameAvailableAt,
      releaseScheduleLabel: prev.releaseScheduleLabel,
      releaseScheduleTimeZone: prev.releaseScheduleTimeZone,
      textScore: prev.textScore,
      textTotalRounds: prev.textTotalRounds,
      pictureScore: prev.pictureScore,
      pictureTotalRounds: prev.pictureTotalRounds,
    }));
  }, [state.sessionId, checkTodayGameStatus]);

  const continueChallengeAfterPopup = useCallback(() => {
    setState(prev => ({
      ...prev,
      showChallengeCompletedPopup: false,
      challengeCompletedAndContinuing: true,
    }));
  }, []);

  const finishGameFromPopup = useCallback(() => {
    setState(prev => ({
      ...prev,
      showChallengeCompletedPopup: false,
      gameFinished: true,
    }));

    // If this is a daily game, save results
    if (state.dailyGameData && state.sessionId) {
      const saveResults = async () => {
        try {
          const accuracy = Math.round((state.score / state.userSelections.length) * 100);

          await discordFetch(API_ENDPOINTS.DAILY_GAME_RESULTS, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-session-id': state.sessionId!
            },
            body: JSON.stringify({
              dailyGameSetId: state.currentlyPlayingGameSetId || state.dailyGameData!.gameSet.id,
              score: state.score,
              totalRounds: state.userSelections.length,
              accuracy,
              selections: state.userSelections
            })
          });

          // Clear saved progress when game is completed
          clearGameProgress('text');
          setState(prev => ({
            ...prev,
            hasPlayedToday: true,
          }));

          // Mark as played today
          const today = new Date().toDateString();
          localStorage.setItem('lastPlayDate', today);
        } catch (error) {
          console.error('Error saving game result:', error);
        }
      };

      saveResults();
    }
  }, [state.dailyGameData, state.sessionId, state.score, state.userSelections, state.currentlyPlayingGameSetId]);

  return {
    state,
    startGame,
    startPictureGame,
    makeSelection,
    makePictureSelection,
    resetGame,
    resetToMenu,
    resumeGame,
    hasSavedTextProgress,
    hasSavedPictureProgress,
    getAccuracy,
    isLoading,
    checkForNewContent,
    startDailyGame,
    gameConfig,
    fetchGameConfig,
    clearOldGameAndStartFresh,
    continueChallengeAfterPopup,
    finishGameFromPopup,
  };
};

const EASTERN_TIME_ZONE = 'America/New_York';

const offsetRegex = /(GMT|UTC)(?:(\+|-)(\d{1,2})(?::(\d{2}))?)?/;

const parseOffsetMinutes = (label: string): number => {
  const match = label.match(offsetRegex);
  if (!match) {
    return 0;
  }

  const sign = match[2] === '-' ? -1 : 1;
  const hours = match[3] ? parseInt(match[3], 10) : 0;
  const minutes = match[4] ? parseInt(match[4], 10) : 0;
  return sign * (hours * 60 + minutes);
};

const getTimeZoneOffsetMinutes = (date: Date, timeZone: string): number => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset',
    });

    const tzLabel =
      formatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value || 'GMT';

    return parseOffsetMinutes(tzLabel);
  } catch (error) {
    console.warn('Failed to calculate timezone offset, defaulting to UTC', error);
    return 0;
  }
};

const getZonedDateParts = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const parts = formatter.formatToParts(date);
  const getPartValue = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value || 0);

  return {
    year: getPartValue('year'),
    month: getPartValue('month'),
    day: getPartValue('day'),
  };
};

const buildEasternMidnightUtc = (reference: Date): Date => {
  const { year, month, day } = getZonedDateParts(reference, EASTERN_TIME_ZONE);
  // Use midday to capture the offset for that calendar day (covers DST transitions)
  const middayUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const offsetMinutes = getTimeZoneOffsetMinutes(middayUtc, EASTERN_TIME_ZONE);
  const midnightUtcTimestamp =
    Date.UTC(year, month - 1, day, 0, 0, 0) - offsetMinutes * 60 * 1000;

  return new Date(midnightUtcTimestamp);
};

const getNextEasternMidnight = (reference: Date): Date => {
  const midnight = buildEasternMidnightUtc(reference);
  // Add 24 hours
  return new Date(midnight.getTime() + 24 * 60 * 60 * 1000);
};

const computeNextGameAvailableAt = (
  nextReleaseAt: Date | string | undefined,
  gameSetDate: Date | undefined
): Date | undefined => {
  // 1. If backend provides nextReleaseAt, use it.
  if (nextReleaseAt) {
    return new Date(nextReleaseAt);
  }

  // 2. Fallback: Calculate next midnight ET based on current time
  return getNextEasternMidnight(new Date());
};
